import { takeLatest, put, all, call } from 'redux-saga/effects';
import DashboardForDepartmentActionTypes from './dashboardfordepartment.types';
import { getDashboardForDepartmentData } from './dashboardfordepartment.services';
import {
	dashboardForDepartmentSuccess,
	dashboardForDepartmentFailure,
	dashboardForDepartmentAttendanceBySectionwiseFailure,
	dashboardForDepartmentAttendanceBySectionwiseSuccess,
} from './dashboardfordepartment.action';
import axios from 'axios';
import { API_URL } from '../config';

interface SectionData {
	sectionName: string;
	currentmonth?: number;
	year?: number;
	totalactiveemployeesinsection?: number;
	totalcheckins?: number;
	on_leave?: number;
	late_checkin?: number;
	early_checkout?: number;
}

export function* fetchDashboardForDepartmentData(): Generator<any, void, any> {
	try {
		const response = yield call(getDashboardForDepartmentData);
		if (response.data.status === 'success') {
			yield put(dashboardForDepartmentSuccess(response.data));
		} else {
			yield put(
				dashboardForDepartmentFailure(response.data.message || 'Failed to fetch data'),
			);
		}
	} catch (error: any) {
		yield put(
			dashboardForDepartmentFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}
export function* fetchDashboardForDepartmentAttendanceBySectionwiseData(
	action: any,
): Generator<any, void, any> {
	try {
		const { sectionName, currentmonth, year } = action.payload;

		// Validate inputs
		if (!sectionName || !currentmonth || !year) {
			yield put(
				dashboardForDepartmentAttendanceBySectionwiseFailure(
					'Section name, month and year are required',
				),
			);
			return;
		}

		// Ensure month is a number between 1-12
		const monthNum = parseInt(currentmonth, 10);
		if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
			yield put(dashboardForDepartmentAttendanceBySectionwiseFailure('Invalid month value'));
			return;
		}

		// Ensure year is valid
		const yearNum = parseInt(year, 10);
		if (isNaN(yearNum) || yearNum < 2000 || yearNum > 2100) {
			yield put(dashboardForDepartmentAttendanceBySectionwiseFailure('Invalid year value'));
			return;
		}

		console.log('Fetching attendance data for:', {
			sectionName,
			currentmonth: monthNum,
			year: yearNum,
		});
		const response = yield call([axios, 'get'], `${API_URL}DashboardAttendanceForHeadOffice`, {
			params: {
				sectionName,
				currentmonth: monthNum.toString(),
				year: yearNum.toString(),
			},
		});
		console.log('API Response:', response);

		// Check if response is successful and contains data
		if (response.status === 200) {
			// Handle both array and object response formats
			const responseData = Array.isArray(response.data) ? response.data : response.data.data;

			if (!responseData || !Array.isArray(responseData)) {
				yield put(
					dashboardForDepartmentAttendanceBySectionwiseFailure(
						'Invalid data format received from server',
					),
				);
				return;
			}

			console.log('Response Data:', responseData);

			// Find the specific section data
			const sectionData = responseData.find((item: SectionData) => {
				const itemMonth = Number(item.currentmonth);
				const itemYear = Number(item.year);

				console.log('Comparing section data:', {
					itemSection: item.sectionName,
					itemMonth,
					itemYear,
					searchSection: sectionName,
					searchMonth: monthNum,
					searchYear: yearNum,
					isSectionMatch: item.sectionName === sectionName,
					isMonthMatch: itemMonth === monthNum,
					isYearMatch: itemYear === yearNum,
				});

				return (
					item.sectionName === sectionName &&
					itemMonth === monthNum &&
					itemYear === yearNum
				);
			});

			if (sectionData) {
				console.log('Found matching section data:', sectionData);

				// Parse all numeric values from the response
				const totalactiveemployeesinsection = parseInt(
					sectionData.totalactiveemployeesinsection?.toString() || '0',
					10,
				);
				const lateCheckin = parseInt(sectionData.late_checkin?.toString() || '0', 10);
				const earlyCheckout = parseInt(sectionData.early_checkout?.toString() || '0', 10);
				const totalcheckins = parseInt(sectionData.totalcheckins?.toString() || '0', 10);
				const on_leave = parseInt(sectionData.on_leave?.toString() || '0', 10);

				console.log('Parsed values:', {
					totalactiveemployeesinsection,
					lateCheckin,
					earlyCheckout,
					totalcheckins,
					on_leave,
					rawLateCheckin: sectionData.late_checkin,
					rawOnLeave: sectionData.on_leave,
					rawTotalCheckins: sectionData.totalcheckins,
					rawTotalEmployees: sectionData.totalactiveemployeesinsection,
				});

				// Create attendance data with parsed values
				const attendanceData = {
					sectionName: sectionData.sectionName,
					currentmonth: monthNum,
					year: yearNum,
					totalactiveemployeesinsection: totalactiveemployeesinsection,
					totalcheckins: totalcheckins,
					on_leave: on_leave,
					late_checkin: lateCheckin,
					early_checkout: earlyCheckout,
				};

				// Log the attendance data for debugging
				console.log('Processed attendance data:', attendanceData);

				const successPayload = {
					status: 'success',
					message: 'Data retrieved successfully',
					data: {
						data: [attendanceData],
					},
				};

				console.log('Creating success payload:', successPayload);
				const successAction =
					dashboardForDepartmentAttendanceBySectionwiseSuccess(successPayload);
				console.log('Dispatching success action:', successAction);
				yield put(successAction);
			} else {
				// If no data found for the section, return default values
				const defaultData = {
					sectionName: sectionName,
					currentmonth: monthNum,
					year: yearNum,
					totalactiveemployeesinsection: 0,
					totalcheckins: 0,
					on_leave: 0,
					late_checkin: 0,
					early_checkout: 0,
				};

				console.log('No matching data found, using default:', defaultData);

				const defaultAction = dashboardForDepartmentAttendanceBySectionwiseSuccess({
					status: 'success',
					message: 'No attendance data found for the selected section',
					data: {
						data: [defaultData],
					},
				});

				console.log('Dispatching default action:', defaultAction);
				yield put(defaultAction);
			}
		} else {
			yield put(
				dashboardForDepartmentAttendanceBySectionwiseFailure(
					'Invalid response from server',
				),
			);
		}
	} catch (error: any) {
		console.error('Attendance data fetch error:', error);
		console.error('Error details:', {
			message: error?.message,
			response: error?.response?.data,
			status: error?.response?.status,
		});

		const errorMessage =
			error?.response?.data?.message ||
			error?.message ||
			'An error occurred while fetching attendance data';
		yield put(dashboardForDepartmentAttendanceBySectionwiseFailure(errorMessage));
	}
}

export function* onDashboardForDepartmentSagaStart(): Generator<any, void, any> {
	yield takeLatest(
		DashboardForDepartmentActionTypes.DASHBOARDFORDEPARTMENT_START,
		fetchDashboardForDepartmentData,
	);
}

export function* onDashboardForDepartmentAttendanceBySectionwiseSagaStart(): Generator<
	any,
	void,
	any
> {
	yield takeLatest(
		DashboardForDepartmentActionTypes.DASHBOARDFORDEPARTMENT_ATTENDANCE_BY_SECTIONWISE_START,
		fetchDashboardForDepartmentAttendanceBySectionwiseData,
	);
}

export function* dashboardfordepartmentSaga(): Generator<any, void, any> {
	yield all([
		call(onDashboardForDepartmentSagaStart),
		call(onDashboardForDepartmentAttendanceBySectionwiseSagaStart),
	]);
}
